import { inject, Injectable, signal } from "@angular/core";
import { UserClient } from "@clients/firestore/user.client";
import { REWARD_CURSOR_PROGRESSION_DEFAULT_ORDER } from "@config";
import { RewardCursors } from "@domain/RewardCursors";
import { RewardCursor, User } from "@domain/User";
import { DeviceCapabilityService } from "@services/device-capability.service";
import { AuthState } from "@states/auth.state";

@Injectable({
  providedIn: "root",
})
export class RewardService {
  private authState = inject(AuthState);
  private deviceCapabilityService = inject(DeviceCapabilityService);
  private userClient = inject(UserClient);

  isRewardMechanismEnabled = signal(!this.deviceCapabilityService.hasTouchCapabilities());
  private rewardCursorsInOrder = REWARD_CURSOR_PROGRESSION_DEFAULT_ORDER;

  async selectCursorByName(cursor: RewardCursor) {
    const currentUser = this.authState.currentUser();
    if (!currentUser) return;

    this.saveUserRewardMechanism({
      ...currentUser,
      rewardCursors: new RewardCursors({
        lastAvailableCursor: currentUser?.rewardCursors.getLastAvailableCursor(),
        currentCursor: cursor,
      }),
      showRewardMechanism: true,
    });
  }

  async selectNewUnblockedCursor() {
    const currentUser = this.authState.currentUser();
    if (!currentUser) return;

    const lastAvailableCursor = currentUser.rewardCursors.getLastAvailableCursor();
    this.saveUserRewardMechanism({
      ...currentUser,
      rewardCursors: new RewardCursors({
        lastAvailableCursor: lastAvailableCursor,
        currentCursor: lastAvailableCursor,
      }),
      showRewardMechanism: true,
    });
  }

  async showRewardMechanism() {
    const currentUser = this.authState.currentUser();
    if (!currentUser) return;

    this.saveUserRewardMechanism({
      ...currentUser,
      showRewardMechanism: true,
    });
  }

  async acknowledgeRewardMechanism() {
    const currentUser = this.authState.currentUser();
    if (!currentUser) return;

    await this.saveUserRewardMechanism({
      ...currentUser,
      hasAcknowledgedRewardCursor: true,
    });
  }

  async hideRewardMechanism() {
    const currentUser = this.authState.currentUser();

    if (!currentUser) return;

    await this.saveUserRewardMechanism({
      ...currentUser,
      showRewardMechanism: false,
    });
  }

  incrementCurrentUserCorrectAnswerCount() {
    const currentUser = this.authState.currentUser();

    if (!currentUser) return;

    const lastAvailableCursor = currentUser.rewardCursors.getLastAvailableCursor();
    if (lastAvailableCursor === this.rewardCursorsInOrder[this.rewardCursorsInOrder.length - 1]) return;

    const currentUserCorrectAnswerCount = currentUser.nextRewardCursorCorrectAnswerCount;
    const newCurrentUserCorrectAnswerCount = currentUserCorrectAnswerCount + 1;

    if (newCurrentUserCorrectAnswerCount < 24) {
      this.saveUserRewardMechanism({
        ...currentUser,
        nextRewardCursorCorrectAnswerCount: newCurrentUserCorrectAnswerCount,
      });
      return;
    }

    return this.unblockNewRewardCursor();
  }

  private unblockNewRewardCursor() {
    const currentUser = this.authState.currentUser();
    if (!currentUser) return;

    const newAvailableCursor = currentUser.rewardCursors.getNextCursorToUnlock();

    if (!newAvailableCursor) {
      return undefined;
    }

    this.saveUserRewardMechanism({
      ...currentUser,
      rewardCursors: new RewardCursors({ lastAvailableCursor: newAvailableCursor, currentCursor: currentUser.rewardCursors.getCurrentCursor() }),
      nextRewardCursorCorrectAnswerCount: 0,
    });

    return newAvailableCursor;
  }

  private async saveUserRewardMechanism(newCurrentUser: User) {
    this.authState.setCurrentUser(newCurrentUser);
    this.userClient.updateUser(newCurrentUser);
  }
}
